'use client';

import { useEffect, useState } from 'react';
import {
  captureCrashAndGetReference,
  CRASH_SCREEN_HEADING,
  CRASH_SCREEN_BODY_WITH_CODE,
  CRASH_SCREEN_BODY_NO_CODE,
} from '@/utils/observability/crashReference';

/**
 * The App Router route-segment boundary — the "something went wrong" screen for
 * every route. It SHADOWS global-error.tsx, which only fires for root-layout
 * crashes, so before #3253 the most common customer-visible failure in the
 * dashboard reported to nobody.
 *
 * Server-component crashes are captured twice on purpose: once server-side by
 * `src/instrumentation.ts` with the real stack, and once here with Next's
 * masked "An error occurred in the Server Components render" message. The
 * second is what carries the digest to the browser and gives the user a code.
 * Both sides stamp the same `nextDigest` tag so one search returns both.
 *
 * Reference-code rules live in utils/observability/crashReference.ts and are
 * shared with global-error.tsx — the two screens must not diverge.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [reference, setReference] = useState<string | null>(null);

  useEffect(() => {
    console.error(error);
    setReference(captureCrashAndGetReference(error));
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            {CRASH_SCREEN_HEADING}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            {reference ? CRASH_SCREEN_BODY_WITH_CODE : CRASH_SCREEN_BODY_NO_CODE}
          </p>
          {reference && (
            <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-500">
              Reference code:{' '}
              <span className="font-mono select-all break-all">{reference}</span>
            </p>
          )}
        </div>
        <div className="text-center">
          <button
            onClick={reset}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
